/**
 * RadarChart Component
 * Reusable radar chart for multi-dimensional data comparison
 */

"use client";

import {
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
} from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { BaseChartData, RadarChartProps } from "./types";
import { formatAxisTick } from "./utils";

export const RadarChart = ({
  data,
  config,
  dataKeys,
  angleKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height = 350,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  tooltipConfig,
  legendConfig,
  fillOpacity = 0.6,
  strokeWidth = 2,
  showDots = true,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
}: RadarChartProps) => {
  // Wrapper functions for formatAxisTick to match Recharts signature
  const formatAngleTick = (value: any) => formatAxisTick(value);
  const formatRadiusTick = (value: any) => formatAxisTick(value, true);

  const chartContent = (
    <RechartsRadarChart accessibilityLayer={accessibilityLayer} data={data}>
      {showGrid && <PolarGrid />}
      <PolarAngleAxis dataKey={angleKey} tickFormatter={formatAngleTick} />
      <PolarRadiusAxis angle={90} tickFormatter={formatRadiusTick} />
      {showTooltip && (
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              indicator={tooltipConfig?.indicator ?? "line"}
              hideLabel={tooltipConfig?.hideLabel}
              nameKey={tooltipConfig?.nameKey}
            />
          }
        />
      )}
      {dataKeys.map((key: keyof BaseChartData, index: number) => {
        const configKey = String(key);

        // Use color from config, fallback to dynamic chart color if not defined
        const configColor = config[configKey]?.color;
        const fallbackChartNumber = (index % 5) + 1;
        const chartVariable =
          configColor || `var(--chart-${fallbackChartNumber})`;

        return (
          <Radar
            key={configKey}
            dataKey={key}
            fill={chartVariable}
            fillOpacity={fillOpacity}
            stroke={chartVariable}
            strokeWidth={strokeWidth}
            dot={showDots ? { r: 4 } : false}
          />
        );
      })}{" "}
      {showLegend && (
        <ChartLegend
          content={<ChartLegendContent />}
          className="-translate-y-2"
        />
      )}
    </RechartsRadarChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};

RadarChart.displayName = "RadarChart";

export default RadarChart;
